import { useMemo } from 'react'
import { useUser } from '../providers/UserProvider'
import { useClientOptions } from '../providers/ClientOptionsProvider'

export interface AccountSettingsPermissionUser {
  hasPermissions?: (permissions: string[], requireAll?: boolean) => boolean
  isAdmin?: boolean
  isSuperadmin?: boolean
  isSupport?: boolean
  isCorporate?: boolean
}

/**
 * Canonical "can this user edit account settings?" check, shared by
 * `useUserPermissions` and by class components that only hold the user context
 * (and so can't call the hook). Keep this the single definition - an inlined
 * copy of the privileged-role list drifts and opens a bypass window.
 */
export function hasAccountSettingsPermission(user: AccountSettingsPermissionUser = {}) {
  const { hasPermissions, isAdmin, isSuperadmin, isSupport, isCorporate } = user

  return !!(hasPermissions?.(['write:account-settings']) || isAdmin || isSuperadmin || isSupport || isCorporate)
}

// User related permissions hook
export const useUserPermissions = () => {
  const { isAdmin: isClientAdmin } = useClientOptions()
  const {
    hasPermissions = () => isClientAdmin,
    user,
    isAdmin,
    isSuperadmin,
    isSupport,
    isCorporate,
    isCore,
    isTeam,
    isPainter,
    isSales
  } = useUser()
  const isPrivileged = isAdmin || isSuperadmin || isSupport || isCorporate
  const isNewRoleUser = !!(isCore || isTeam || isPainter)

  const permissionToViewAllQuotes = hasPermissions(['read:all-quotes'])
  const permissionToCreateQuote = hasPermissions(['create:quotes'])
  const permissionToCreateInvoice = hasPermissions(['create:invoices'])
  const permissionToCreateContact = hasPermissions(['create:contacts'])
  const permissionToCreateCompany = hasPermissions(['create:companies'])
  const permissionToCreateDeal = hasPermissions(['create:deals'])
  const permissionToCreateEvent = hasPermissions(['create:events'])
  const permissionToCreateTask = hasPermissions(['create:tasks'])

  const permissionToSendAssignedQuote = hasPermissions(['send:assigned-quotes'])
  const permissionToEditAssignedQuote = hasPermissions(['write:assigned-quotes'])
  const permissionToEditAllQuote = hasPermissions(['write:all-quotes'])
  const permissionToSendAllQuote = hasPermissions(['send:all-quotes'])
  const permissionToSendAssignedInvoice = hasPermissions(['send:assigned-invoices'])
  const permissionToSendAllInvoice = hasPermissions(['send:all-invoices'])
  const permissionToEditAssignedInvoice = hasPermissions(['write:assigned-invoices'])
  const permissionToEditAllInvoice = hasPermissions(['write:all-invoices'])

  const permissionToEditAllCompanies = hasPermissions(['write:all-companies'])
  const permissionToEditAssignedCompanies = hasPermissions(['write:assigned-companies'])

  const permissionToEditAllContacts = hasPermissions(['write:all-contacts'])
  const permissionToEditAssignedContacts = hasPermissions(['write:assigned-contacts'])

  const permissionToEditAllDeals = hasPermissions(['write:all-deals'])
  const permissionToEditAssignedDeals = hasPermissions(['write:assigned-deals'])

  const permissionToEditAllEvents = hasPermissions(['write:all-events'])
  const permissionToEditAssignedEvents = hasPermissions(['write:assigned-events'])

  const permissionToEditAllTasks = hasPermissions(['write:all-tasks'])
  const permissionToEditAssignedTasks = hasPermissions(['write:assigned-tasks'])

  const permissionToViewEstimator = hasPermissions(['read:estimator-select'])

  const permissionToAssignDocuments = hasPermissions(['assign:assigned-documents', 'assign:all-documents'], true)
  const permissionToViewPresentations = hasPermissions(
    ['write-presentation-section:assigned-quotes', 'write-presentation-section:all-quotes'],
    true
  )

  const permissionToExport = hasPermissions(['export:data']) || isPrivileged
  // `isPrivileged` is undefined when no role flag is set, so coerce — consumers gate rendering on this.
  const permissionToImport = !!(hasPermissions(['import:data']) || isPrivileged)
  const permissionToViewReports = hasPermissions(['read:reports']) || isPrivileged
  const permissionToReadAllDocuments = hasPermissions(['read:all-documents']) || isPrivileged
  const permissionToWriteAllDocuments = hasPermissions(['write:all-documents']) || isPrivileged
  const permissionToEditAccountSettings = hasAccountSettingsPermission({
    hasPermissions,
    isAdmin,
    isSuperadmin,
    isSupport,
    isCorporate
  })
  const permissionToManageUsers = hasPermissions(['write:account-settings-user-management']) || isPrivileged
  const permissionToUpdateBilling = hasPermissions(['write:account-settings-billing']) || isPrivileged
  const permissionToViewAllTasks = hasPermissions(['read:all-tasks'])
  const permissionToViewAllEvents = hasPermissions(['read:all-events'])
  const permissionToViewAllDeals = hasPermissions(['read:all-deals'])
  const permissionToViewFollowUps = isPrivileged || isCore || isSales
  const permissionToViewDashboard = isPrivileged || !isNewRoleUser || hasPermissions(['read:dashboard'])

  return useMemo(
    () => ({
      permissionToViewAllQuotes,
      permissionToCreateQuote,
      permissionToCreateInvoice,
      permissionToSendAssignedQuote,
      permissionToEditAssignedQuote,
      permissionToEditAllQuote,
      permissionToSendAllQuote,
      permissionToSendAssignedInvoice,
      permissionToSendAllInvoice,
      permissionToEditAssignedInvoice,
      permissionToEditAllInvoice,

      permissionToCreateContact,
      permissionToEditAllContacts,
      permissionToEditAssignedContacts,

      permissionToCreateCompany,
      permissionToEditAllCompanies,
      permissionToEditAssignedCompanies,

      permissionToCreateDeal,
      permissionToEditAllDeals,
      permissionToEditAssignedDeals,

      permissionToCreateTask,
      permissionToEditAllTasks,
      permissionToEditAssignedTasks,

      permissionToCreateEvent,
      permissionToEditAllEvents,
      permissionToEditAssignedEvents,

      permissionToEditAccountSettings,
      permissionToViewEstimator,
      permissionToAssignDocuments,
      permissionToViewReports,
      permissionToViewPresentations,
      permissionToExport,
      permissionToImport,
      permissionToUpdateBilling,
      permissionToManageUsers,
      permissionToViewAllTasks,
      permissionToViewAllEvents,
      permissionToViewAllDeals,
      permissionToReadAllDocuments,
      permissionToWriteAllDocuments,
      permissionToViewFollowUps,
      permissionToViewDashboard
    }),
    [user, hasPermissions, isAdmin, isSuperadmin, isSupport, isCorporate, isCore, isTeam, isPainter]
  )
}
